import { getEmployeeStorage, isSuperUserEmployee } from 'auth/local-storage';
import Helper from 'library/Helper';
import moment from 'moment';
import { last, isNil, isEmpty } from 'lodash';
import {
    transformHighValue,
    transformPiecesDetailNew,
    transformAddressNew,
    transformInvoicesDetailNew,
} from '../consignment-helper';
import { createConditionalObject } from '../../common/utils';

export const isEmptyValue = (value: any) => isNil(value) || value === '';

const getCreatedBy = (isSuperUser: boolean, updatedData: any, employee: any, options: any) => {
    if (isSuperUser) {
        return `${employee.employeeCode} (Employee)`;
    }

    if (options?.isCustomerUser) {
        return `${options.customerUserName} (Customer User)`;
    }
    return `${updatedData?.childClient?.split('+')[0]} (Customer)`;
};

const validatedCodAmount = (value: any) => {
    if (value === '' || value === null) {
        return null;
    }
    const result = Number(value);
    if (Number.isNaN(result)) {
        return value;
    }
    return result;
};

export const transformPayloadNew = (updatedData?: any, options: Record<any, any> = {}) => {
    const {
        showRiskSurchargeType,
        isCustomFieldAllowed,
        customFieldsList,
        isCodFieldPresent,
        isCustomForm,
        isServiceableForm = false,
        allowChildLoginOnParent,
        isCustomerUser,
        customerUserName,
    } = options;
    const pickupTime = updatedData?.pickupTime?.split('-');
    const isCOD = updatedData.isCOD === 'yes';
    const employee = getEmployeeStorage();
    const isSuperUser = isSuperUserEmployee();

    let weight;
    if (isCustomForm) {
        weight = isEmptyValue(updatedData.weight)
            ? null : Helper.converttoKilogram(Number(updatedData.weight), updatedData.weightUnit);
    } else {
        weight = Helper.converttoKilogram(Number(updatedData.weight), updatedData.weightUnit);
    }
    let customFields: any = {};
    if (isCustomFieldAllowed && Array.isArray(customFieldsList)) {
        const customFieldsIds: string[] = customFieldsList.map((item: any) => item.id);
        customFields = Object.entries(updatedData)
            .filter(([key]) => customFieldsIds?.includes(key))
            .map(([key, value]) => {
                return [key, value];
            });
    }

    let piecesDetail: any[] = [];
    if (!isEmpty(updatedData.piecesDetail)) {
        piecesDetail = updatedData.piecesDetail;
    } else if (!isEmpty(updatedData.pieces)) {
        piecesDetail = updatedData.pieces;
    }
    const isStaticFormForPiecesDetail = isEmpty(updatedData.piecesDetail) && !isEmpty(updatedData.pieces);

    let courierType = updatedData.courierType;
    if ([null, ''].includes(updatedData.courierType)) {
        courierType = 'non-document';
    }

    if (customFields.length) {
        customFields = Object.fromEntries(customFields);
    }
    const isInternational = Helper.sanitizeStringCodeLowerCase(updatedData.consignmentCategory) === 'international';
    const isNonDoc = (courierType || '').toLowerCase() === 'non-document';
    const createdBy = getCreatedBy(isSuperUser, updatedData, employee, { isCustomerUser, customerUserName });
    const codAmount = validatedCodAmount(updatedData.codAmount);
    const customerCode = updatedData.childClient?.split('+')[0];
    const customerId = updatedData.childClient?.split('+')[1];
    const customsValue = createConditionalObject(updatedData, {
        amount: 'customsAmount',
        currency: 'customsCurrency', // from excel
        import_reference_number: 'importReferenceNumber',
        export_reference_number: 'exportReferenceNumber',
        is_customs_declarable: (data: any) => (
            Helper.sanitizeStringCodeLowerCase(data.isCustomsDeclarable) === 'true' || undefined
        ),
    });
    let deliveryDate = updatedData?.deliveryDate;
    let deliveryStartTime = updatedData?.deliveryTime?.[0];
    let deliveryEndTime = updatedData?.deliveryTime?.[1];

    if (options?.currentSlotData && !options?.previousSlotData) {
        if (!deliveryDate) {
            deliveryDate = options.currentSlotData.deliveryDate;
        }
        if (!deliveryStartTime && !deliveryEndTime) {
            deliveryStartTime = options.currentSlotData.timeSlot?.[0];
            deliveryEndTime = options.currentSlotData.timeSlot?.[1];
        }
    }

    if (deliveryStartTime && moment.isMoment(deliveryStartTime)) {
        deliveryStartTime = deliveryStartTime.format('HH:mm:ss');
    }
    if (deliveryEndTime && moment.isMoment(deliveryEndTime)) {
        deliveryEndTime = deliveryEndTime.format('HH:mm:ss');
    }
    const payload: any = {
        ...customFields,
        source: options?.source || undefined,
        custom_fields: customFields,
        reference_number: Helper.sanitizeStringCode(updatedData.referenceNumber),
        customer_details: {
            client_code: customerCode,
            code: customerCode,
            id: customerId,
        },
        customer_code: customerCode,
        customer_id: customerId,
        service_type: !isServiceableForm ? updatedData.serviceType : undefined,
        // serviceability api also calls getAllServiceTypes (is_active: true) &
        // internally we also hit this endpoint in saveMultiple in obbv2. Fe sends service_type_id
        service_type_id: !isServiceableForm ? updatedData.serviceType : undefined,
        booking_service_type: !isServiceableForm ? updatedData.serviceType : undefined,
        creation_time: !isServiceableForm ? new Date().toISOString() : undefined,
        origin_details: !isEmpty(updatedData.srcAddress) ? transformAddressNew(updatedData.srcAddress) : '',
        destination_details: !isEmpty(updatedData.dstAddress) ? transformAddressNew(updatedData.dstAddress) : '',
        return_details: !isEmpty(updatedData.retAddress) ? transformAddressNew(updatedData.retAddress) : '',
        exceptional_return_details: !isEmpty(updatedData.exceptionalRetAddress)
            ? transformAddressNew(updatedData.exceptionalRetAddress) : '',
        alternate_delivery_details_1: !isEmpty(updatedData.alternateDelAddress1)
            ? transformAddressNew(updatedData.alternateDelAddress1) : '',
        alternate_delivery_details_2: !isEmpty(updatedData.alternateDelAddress2)
            ? transformAddressNew(updatedData.alternateDelAddress2) : '',
        redirection_address: !isEmpty(updatedData.redirectionAddress)
            ? transformAddressNew(updatedData.redirectionAddress) : '',
        billing_details: !isEmpty(updatedData.billToDetails) ? transformAddressNew(updatedData.billToDetails) : '',
        is_risk_surcharge_applicable: updatedData.isRiskSurchargeApplicable,
        risk_surcharge_type: showRiskSurchargeType && isNonDoc ? updatedData.riskSurchargeType : undefined,
        weight: isServiceableForm && !Helper.isNeitherNullNorUndefined(weight) ? undefined : weight,
        weight_unit: 'kg',
        dimension_unit: 'cm',
        courier_type: !isServiceableForm ? Helper.sanitizeStringCode(courierType) : courierType,
        load_type: Helper.sanitizeStringCode(courierType),
        notes: (updatedData.deliveryInstructions && !isServiceableForm)
            ? `${updatedData.notes ? updatedData.notes : ''}${updatedData.deliveryInstructions}` : (updatedData.notes),
        action_type: updatedData.actionType,
        remarks: '',
        customer_reference_number: !isServiceableForm
            ? Helper.sanitizeStringCode(updatedData.customerReferenceNumber) : updatedData.customerReferenceNumber,
        prepaid_amount: !isServiceableForm ? 0 : undefined,
        is_cod: !!(!isCodFieldPresent || isCOD ? codAmount : undefined),
        cod_amount: !isCodFieldPresent || isCOD ? codAmount : undefined,
        cod_favor_of: isCOD ? updatedData.codFavorOf : undefined,
        cod_collection_mode: isCOD ? (updatedData.codCollectionMode || null) : undefined,
        description: updatedData.description,
        // delivery_charge: updatedData.deliveryCharge, // Not in single CN form, not support in cp
        // service_code: updatedData.serviceCode, // dtdc specific, no support in cp
        consignment_type: Helper.sanitizeStringCodeLowerCase(updatedData.consignmentType),
        consignee_id: updatedData.consigneeId, // from excel
        rescheduled_date: updatedData.rescheduledDate, // from excel
        rescheduled_timeslot: { // from excel
            start: updatedData.rescheduledStarttime,
            end: updatedData.rescheduledEndtime,
        },
        consignee_email: updatedData.consigneeEmail, // from excel
        src_company_name: updatedData.srcCompanyName, // from excel
        dst_company_name: updatedData.dstCompanyName, // from excel
        currency: updatedData.currency,
        createdBy,
        creator_type: !isServiceableForm ? 'CUSTOMER' : undefined,
        creator_code: !isServiceableForm ? customerCode : createdBy,
        num_pieces: Number(updatedData.numberOfPieces) || (!isServiceableForm ? 1 : undefined),
        type_of_delivery: updatedData.typeOfDelivery,
        kirana_hub_code: updatedData.kiranaHubCode,
        is_international: isInternational,
        international_detail_inco_terms: Helper.sanitizeStringCode(updatedData.incoTerms),
        inco_terms: Helper.sanitizeStringCode(updatedData.incoTerms),
        delivery_terms: Helper.sanitizeStringCode(updatedData.deliveryTerms),
        delivery_instruction: updatedData.deliveryInstruction,
        pickup_instruction: updatedData.pickupInstruction,
        ecom_shipment: updatedData.ecomShipment ? updatedData.ecomShipment === 'true' : undefined,
        meis: updatedData.meis ? updatedData.meis === 'true' : undefined,
        iori_number: updatedData.ioriNumber,
        eori_number: updatedData.eoriNumber,
        against_bond_lut: updatedData.againstBondLut,
        fob_value: updatedData.fobValue,
        fob_value_currency: Helper.sanitizeStringCode(updatedData.fobValueCurrency),
        freight_cost: updatedData.freightCost,
        freight_cost_currency: Helper.sanitizeStringCode(updatedData.freightCostCurrency),
        total_gst_paid_amount: updatedData.totalGSTPaidAmount,
        total_gst_paid_currency: Helper.sanitizeStringCode(updatedData.totalGSTPaidCurrency) || 'INR',
        consignor_kyc_doc_type: updatedData.consignorKycDocType,
        consignor_kyc_doc_number: updatedData.consignorKycDocNumber,
        consignor_kyc_front_image: updatedData.consignorKycFrontImage,
        consignor_kyc_back_image: updatedData.consignorKycBackImage || '',
        is_battery: updatedData.isBattery ? updatedData.isBattery === 'true' : undefined,
        export_invoice_date: updatedData?.invoiceDate?.format('YYYY-MM-DD'),
        consignor_company_name: updatedData.consignorCompanyName,
        consignee_company_name: updatedData.consigneeCompanyName,
        cess_value: updatedData.cessValue,
        origin_type: updatedData.originType,
        consignor_iec_number: updatedData.consignorIECNumber,
        consignor_pan: updatedData.consignorPanId,
        consignor_tax_id: updatedData.consignorTaxId,
        destination_type: updatedData.destinationType,
        nefi_flag: updatedData.nefiFlag ? updatedData.nefiFlag === 'true' : undefined,
        customer_seller_code: updatedData.custSellerCode,
        consignor_gstin_number: updatedData.consignorGSTINNumber,
        cpc_code: updatedData.cpcCode,
        sender_bank_account_number: updatedData.senderBankAccountNumber,
        sender_bank_ad_code: updatedData.senderBankADCode,
        sender_bank_ifsc_code: updatedData.senderBankIFSCCode,
        purpose: updatedData.purpose,
        international_detail_shipment_purpose: courierType === 'non-document'
            ? Helper.sanitizeStringCode(updatedData.shipmentPurpose) : updatedData.shipmentPurposeDoc,
        eor_details: updatedData.eorDetails, // from excel
        ior_details: updatedData.iorDetails, // from excel
        receiver_vat_number: updatedData.receiverVATNumber,
        manifest_number: Helper.sanitizeStringCode(updatedData.manifestNumber), // from excel
        // collect_import_vat: updatedData.collectImportVat, // not supported in CP
        // courier_partner: Helper.sanitizeStringCode(updatedData.courierPartner), // not in CP
        // courier_partner_reference_number: updatedData.courierPartnerReferenceNumber, // not in CP
        is_self_pickup: updatedData.isSelfPickup || false, // not in CP but default value is passed as false
        is_self_delivery: updatedData.isSelfDelivery || false, // not in CP but default value is passed as false

        pieces_detail: !isEmpty(piecesDetail) ? transformPiecesDetailNew(piecesDetail, {
            ...options, isInternational, courierType, isStaticFormForPiecesDetail,
        }) : '',
        invoice_number_list: !isEmpty(updatedData.invoicesDetail)
            ? transformInvoicesDetailNew(updatedData.invoicesDetail) : '',

        movement_type: Helper.sanitizeStringCodeLowerCase(updatedData.movementType),
        // src_pincode_code: updatedData.srcPincodeCode, // not in CP
        // dst_pincode_code: updatedData.dstPincodeCode, // not in CP
        pickup_time_slot_start: pickupTime ? pickupTime[0]?.trim() : undefined,
        pickup_time_slot_end: pickupTime ? pickupTime[1]?.trim() : undefined,
        pickup_service_time: updatedData.pickupServiceTime,
        pickup_date: moment(updatedData.pickupSchedule).format('YYYY-MM-DD'),
        // delivery_service_time: updatedData.deliveryServiceTime, // from excel
        delivery_date: moment(deliveryDate).format('YYYY-MM-DD'),
        delivery_time_slot_start: deliveryStartTime
            ? `${moment(deliveryDate).format('YYYY-MM-DD')} ${deliveryStartTime}`
            : undefined,
        delivery_time_slot_end: deliveryEndTime
            ? `${moment(deliveryDate).format('YYYY-MM-DD')} ${deliveryEndTime}`
            : undefined,
        // app_transaction_id: Tech doc: handled in prequeue
        high_value_details: !isEmpty(updatedData.highValueDetails)
            ? transformHighValue(updatedData.highValueDetails) : '',
        invoice_number: updatedData.invoiceNumber,
        invoice_date: updatedData?.invoiceDate?.format('YYYY-MM-DD'),
        invoice_type: updatedData.invoiceType,
        sender_gstin: updatedData.senderGstin, // from excel
        receiver_gstin: updatedData.receiverGstin, // from excel
        invoice_link: updatedData.invoiceLink, // from excel
        movement_classification: updatedData.movementClassification, // from excel
        customer_civil_id: updatedData.customerCivilId, // from excel
        hub_code: updatedData.pickupHubCode, // from excel
        receiver_civil_id: updatedData.receiverCivilId, // from excel
        is_fragile: updatedData.isFragile === true
            || false, // forms send false, but excel can send 'true' or 'false' as string

        // *******from excel  starts
        imei_1: updatedData.imei1,
        device_1: updatedData.device1,
        imei_2: updatedData.imei2,
        device_2: updatedData.device2,
        imei_3: updatedData.imei3,
        device_3: updatedData.device3,
        sim_number: updatedData.simNumber,
        cpr_number: updatedData.cprNumber,
        line_meters: updatedData.lineMeters, // from excel
        ak_number: updatedData.akNumber, // from excel
        // from ***** excel ends
        // productTypeCode, shopDetails, orderType, allowInstallation, referenceImageUrl not supported in CP
        sender_partner_id: updatedData.senderPartnerId, // from excel
        receiver_partner_id: updatedData.receiverPartnerId, // from excel
        service_item: updatedData.serviceItem, // from excel
        service_item_dimension_unit: updatedData.serviceItemDimensionUnit, // from excel
        item_quantity: updatedData.itemQuantity, // from excel
        item_unit: updatedData.itemUnit, // from excel

        // jobs_detail: updatedData.jobsDetail, // not supported in CP
        // org_softdata_unique_id: updatedData.orgSoftdataUniqueId, //  not supported in CP
        // day: moment().format('dddd'), // Handled in prequeue or by BE
        // hour_minute: moment().format('HH:mm'), // Handled in prequeue or by BE
        // business_id: // handled in masterallocation
        packaging_type: updatedData.packagingType, // from excel
        pudo_cod_amount: updatedData.pudoCodAmount, // from excel
        creator_customer_code: (allowChildLoginOnParent && customerCode)
            ? Helper.sanitizeStringCode(customerCode) : undefined,
        asn_number: updatedData.asnNumber, // from excel
        // eway_bill_list: Helper.isValidArray(updatedData.ewayBillList)
        //     ? updatedData.ewayBillList : undefined, // not supported in CP
        is_retail_transaction: updatedData.retailTransactionType === 'retailTransactionTrue' || false,
        cop_amount: updatedData.cashonpickup, // update for excel by copAmount
        ewb_number: updatedData.ewbNumber,
        ewb_date: moment(updatedData.ewbDate).format('YYYY-MM-DD'),
        eway_bill: isInternational
            ? updatedData.ewayBillNumber : updatedData.ewayBill,
        // default_origin_hub_code: updatedData.defaultOriginHubCode, // not supported in CP
        // client_id_inventory_used deprecate
        eligible_for_auto_allocation: !isServiceableForm ? false : undefined,
        // SRF_details: updatedData.srfDetails, // not supported in CP
        // shopify_order_id: !isServiceableForm ? updatedData.shopifyOrderId : undefined,  // not supported in CP
        // shopify_fulfillment_id: !isServiceableForm ? updatedData.shopifyFulfillmentId : ,//not supported in CP for cns
        account_type: !isServiceableForm ? 'DP' : undefined,
        // net_weight: updatedData.netWeight, // from excel
        product_code: !isServiceableForm ? updatedData.productCode : undefined,
        vehicle_category: updatedData.vehicleCategory, // Not in custom single form
        // group_code: updatedData.groupCode, // from excel
        customs_value: customsValue,
        invoice_amount: !isServiceableForm ? updatedData.invoiceAmount : undefined,
        consignee_code: updatedData.consigneeCode,
        // hsn_code: updatedData.hsnCode, // from excel, or piece level
        customer_user_id: !isServiceableForm ? window.localStorage.getItem('customerUserId') : undefined,
        // segment: updatedData.segment, // from excel
        // reference_image_list: updatedData.referenceImageList, // from excel
        is_csbv: !isServiceableForm ? updatedData.isCsbv : undefined,
        manufacturing_country: !isServiceableForm ? updatedData.manufacturingCountry : undefined,
        is_dangerous_good: (isCustomForm || isServiceableForm) ? updatedData.isDangerousGood === 'true' : undefined,
        dangerous_good_accessible: updatedData.dangerousGoodAccessible === 'true',
        dangerous_good_un_number: updatedData.dangerousGoodUnNumber,
        dangerous_good_class: updatedData.dangerousGoodClass,
        dangerous_good_name: updatedData.dangerousGoodName,
        dangerous_good_division: updatedData.dangerousGoodDivision,
        dangerous_good_packing_group: updatedData.dangerousGoodPackingGroup,
        dangerous_good_category: updatedData.dangerousGoodCategory,
        dangerous_good_category_type: updatedData.dangerousGoodCategoryType,
        dangerous_good_category_packing_instruction: updatedData.dangerousGoodCategoryPackingInstruction,
        is_stackable: (isCustomForm || isServiceableForm) ? updatedData.isStackable === 'yes' : undefined,
        declared_price: updatedData.declaredValue,
        declared_currency: updatedData.declaredCurrency,
        declared_value: updatedData.declaredValue,
        // origin_hub_code: updatedData.originHubCode, // not supported in CP
        // freight_payment_terms: updatedData.freightPaymentTerms, // not supported in CP
        forward_reference_number: updatedData.forwardReferenceNumber, // Top level FRN
        tax_details: createConditionalObject(updatedData, {
            igst: 'taxDetailsIgst',
            sgst: 'taxDetailsSgst',
            cgst: 'taxDetailsCgst',
            tax_percentage: 'taxDetailsTaxPercentage',
            total_tax: 'taxDetailsTotalTax',
            sender_gstin: 'taxDetailsSenderGstin',
            receiver_gstin: 'taxDetailsRecieverGstin',
        }),
        document_details: !isServiceableForm ? updatedData.documentDetails : undefined,
        unlisted_commodity_name: updatedData.unlistedCommodityName,
        purchase_order_number: updatedData.purchaseOrderNumber,
    };

    if (isNonDoc || isServiceableForm) {
        let length;
        let width;
        let height;
        if (!isCustomForm) {
            length = Helper.converttoCentimeter(updatedData.length, updatedData.unit);
            width = Helper.converttoCentimeter(updatedData.width, updatedData.unit);
            height = Helper.converttoCentimeter(updatedData.height, updatedData.unit);
        } else {
            if (!isEmptyValue(updatedData.length)) {
                length = Helper.converttoCentimeter(updatedData.length, updatedData.unit);
            }
            if (!isEmptyValue(updatedData.width)) {
                width = Helper.converttoCentimeter(updatedData.width, updatedData.unit);
            }
            if (!isEmptyValue(updatedData.height)) {
                height = Helper.converttoCentimeter(updatedData.height, updatedData.unit);
            }
        }
        let DEFAULT_DIMENSION;
        if (!isServiceableForm && isNonDoc) {
            DEFAULT_DIMENSION = 1;
            payload.is_default_weight = !Helper.isNeitherNullNorUndefined(payload.weight);
            payload.weight = payload.weight ? payload.weight : 0.1;
        }
        payload.length = length || DEFAULT_DIMENSION;
        payload.width = width || DEFAULT_DIMENSION;
        payload.height = height || DEFAULT_DIMENSION;

        // For serviceability API, only include dimensions if they have values
        if (isServiceableForm) {
            if (!Helper.isNeitherNullNorUndefined(payload.length) || Number.isNaN(payload.length)) delete payload.length;
            if (!Helper.isNeitherNullNorUndefined(payload.width) || Number.isNaN(payload.width)) delete payload.width;
            if (!Helper.isNeitherNullNorUndefined(payload.height) || Number.isNaN(payload.height)) delete payload.height;
            if (!Helper.isNeitherNullNorUndefined(payload.weight) || Number.isNaN(payload.weight)) delete payload.weight;
        }
        payload.commodity_id = Helper.isValidArray(updatedData.commodityId) ? last(updatedData.commodityId) : undefined;
        payload.given_num_pieces = Number(updatedData.numberOfPieces) || (!isServiceableForm ? 1 : undefined);
    }

    if (isServiceableForm) {
        // To handle location serviceability call in future
        payload.filter_services_without_tat = updatedData.filterServicesWithoutTat;

        payload.is_customs_declarable = updatedData.isCustomsDeclarable === 'true';
        payload.customer_details.code = !payload.customer_details.id ? undefined : payload.customer_details.code;
        payload.customer_details.client_code = !payload.customer_details.id
            ? undefined : payload.customer_details.client_code;
        payload.customer_details.id = payload.customer_details.id
            || (window.localStorage.getItem('isChildCustomer') === 'true'
                ? window.localStorage.getItem('childCustomerId') : window.localStorage.getItem('userId'));
    }

    payload.resource_slot_lock_id = options?.currentSlotData?.resourceSlotLockId;
    payload.previous_resource_slot_lock_id = options?.previousSlotData?.resourceSlotLockId;
    return payload;
};
